import type { ReactNode } from 'react';
import { Icon, type IconName } from '../primitives/Icon';
import { Pressable } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import { useThemeColors } from '../hooks/useThemeColors';

export interface ChoiceGridProps {
  /** Rendered above the heading. Lets a caller attach something that
   *  should only appear when the grid does — a divider introducing the
   *  choices, say — without having to predict whether it will render. */
  leading?: ReactNode;
  /** Section heading rendered above the choices. Required so a screen
   *  reader user lands on a meaningful question. */
  heading?: string;
  /** Optional short badge rendered opposite the heading (e.g. "Partner").
   *  Sits on the heading row rather than under the choices so a
   *  disclosure is read before the tap, not after it. */
  badge?: string;
  /** Optional caption beneath the choices (e.g. affiliate disclosure). */
  footer?: ReactNode;
  children: ReactNode;
  /** Override the gap between tiles. Defaults to 10px. */
  gap?: number;
}

/**
 * Generic CTA group. Extracted from `AffiliateChoiceGrid` per ADR-0079 /
 * Phase 6 so other "grid of choices" surfaces (add-place choices,
 * transport-mode picker grid) compose the same chrome.
 *
 * Pass `ChoiceGridItem` children for a two-up tile grid, or a single
 * `ChoiceGridRow` when there is only one choice — see that component for
 * why one tile is the wrong shape.
 */
export function ChoiceGrid({
  leading,
  heading,
  badge,
  footer,
  children,
  gap = 10,
}: ChoiceGridProps) {
  return (
    <YStack gap={12}>
      {leading}
      {heading || badge ? (
        <XStack alignItems="center" gap={10}>
          {heading ? (
            <Text
              fontSize={15}
              fontWeight="700"
              color="$textOnSurface"
              accessibilityRole="header"
              flexShrink={1}
            >
              {heading}
            </Text>
          ) : null}
          {badge ? (
            <Text
              marginLeft="auto"
              flexShrink={0}
              fontSize={10}
              fontWeight="800"
              color="$textSubdued"
              backgroundColor="$surfaceCanvas"
              borderWidth={1}
              borderColor="$borderLight"
              borderRadius={999}
              paddingHorizontal={8}
              paddingVertical={3}
              textTransform="uppercase"
            >
              {badge}
            </Text>
          ) : null}
        </XStack>
      ) : null}
      <XStack flexWrap="wrap" gap={gap}>
        {children}
      </XStack>
      {footer ? (
        <Text fontSize={12} color="$textSubdued" lineHeight={16}>
          {footer}
        </Text>
      ) : null}
    </YStack>
  );
}

export interface ChoiceGridItemProps {
  /** Material icon name rendered in the circle. */
  icon: IconName;
  /** Primary label centered below the icon. */
  label: string;
  /** Optional secondary line below the label. Kept to one line — two
   *  tiles sit side by side, and a description that wraps on one of them
   *  makes the pair uneven. Pass the partner name here and the full
   *  detail to `ChoiceGridRow`, which has the width for it. */
  description?: string;
  onPress: () => void;
  /** Screen-reader label override; defaults to `label`. */
  accessibilityLabel?: string;
  /** Optional override for the tile background. Defaults to
   *  `$surfaceCanvas`. */
  background?: string;
  /** Tint the tile to tie it to a choice the user just made elsewhere
   *  (e.g. the journey mode chip above it). */
  emphasised?: boolean;
  /** Render an external-link glyph in the corner, so a tap that leaves
   *  the app looks like one before it's pressed. */
  external?: boolean;
}

/**
 * Single tile inside `ChoiceGrid`. Composes Pressable + icon badge +
 * label per the extraction documented in ADR-0079.
 *
 *   <ChoiceGrid heading="How are you getting to Tokyo?" badge="Partner">
 *     <ChoiceGridItem icon="train" label="Trains" description="Omio" onPress={…} />
 *     <ChoiceGridItem icon="flight" label="Flights" description="Aviasales" onPress={…} />
 *   </ChoiceGrid>
 */
export function ChoiceGridItem({
  icon,
  label,
  description,
  onPress,
  accessibilityLabel,
  background: backgroundOverride,
  emphasised,
  external,
}: ChoiceGridItemProps) {
  const theme = useThemeColors();
  // Resolve before the cast: Tamagui's colour prop rejects a plain
  // string, but casting first would make the fallback unreachable to the
  // type checker.
  const background = backgroundOverride ?? (emphasised ? '$surfaceInfo' : '$surfaceCanvas');
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      style={({ pressed }) => [
        { flexBasis: '48%', flexGrow: 1 },
        pressed ? { transform: [{ scale: 0.97 }], opacity: 0.9 } : null,
      ]}
    >
      <YStack
        backgroundColor={background as never}
        borderRadius={14}
        borderWidth={1}
        borderColor={emphasised ? '$accentCerulean' : '$borderLight'}
        paddingHorizontal={14}
        paddingVertical={16}
        alignItems="center"
        justifyContent="center"
        gap={8}
        minHeight={96}
      >
        {external ? (
          <YStack position="absolute" top={10} right={10} opacity={0.45}>
            <Icon name="open-in-new" size={14} color={theme.textOnSurface} />
          </YStack>
        ) : null}
        <YStack
          width={40}
          height={40}
          borderRadius={20}
          backgroundColor="$surface"
          alignItems="center"
          justifyContent="center"
        >
          <Icon
            name={icon}
            size={22}
            color={emphasised ? theme.accentCeruleanInk : theme.textOnSurface}
          />
        </YStack>
        <Text
          fontSize={13}
          fontWeight="700"
          color="$textOnSurface"
          numberOfLines={1}
          textAlign="center"
        >
          {label}
        </Text>
        {description ? (
          <Text fontSize={11} color="$textSubdued" numberOfLines={1} textAlign="center">
            {description}
          </Text>
        ) : null}
      </YStack>
    </Pressable>
  );
}

export interface ChoiceGridRowProps {
  icon: IconName;
  label: string;
  /** Secondary line — partner name and what the tap will search. */
  description?: string;
  onPress: () => void;
  accessibilityLabel?: string;
  /** Render an external-link glyph on the trailing edge. */
  external?: boolean;
}

/**
 * Full-width row form of a choice, for when exactly one resolves.
 *
 * `ChoiceGridItem` is built two-up (`flexBasis: 48%`, `minHeight: 96`),
 * so a lone tile stretches the full width and spends 96px of height on
 * one icon and two words. The row carries the same information — plus a
 * description the tile has no room for — in about three quarters of the
 * height, and reads as a control rather than a poster.
 */
export function ChoiceGridRow({
  icon,
  label,
  description,
  onPress,
  accessibilityLabel,
  external,
}: ChoiceGridRowProps) {
  const theme = useThemeColors();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      style={({ pressed }) => [
        { width: '100%' },
        pressed ? { transform: [{ scale: 0.99 }], opacity: 0.9 } : null,
      ]}
    >
      <XStack
        backgroundColor="$surface"
        borderRadius={14}
        borderWidth={1}
        borderColor="$borderLight"
        padding={14}
        alignItems="center"
        gap={14}
      >
        <YStack
          width={44}
          height={44}
          borderRadius={14}
          backgroundColor="$surfaceCanvas"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Icon name={icon} size={22} color={theme.textOnSurface} />
        </YStack>
        <YStack flex={1} minWidth={0} alignItems="flex-start">
          <Text
            fontSize={15}
            fontWeight="700"
            color="$textOnSurface"
            numberOfLines={1}
            textAlign="left"
            width="100%"
          >
            {label}
          </Text>
          {description ? (
            <Text
              fontSize={12}
              color="$textSubdued"
              numberOfLines={1}
              textAlign="left"
              width="100%"
              marginTop={2}
            >
              {description}
            </Text>
          ) : null}
        </YStack>
        {external ? (
          <YStack flexShrink={0} opacity={0.45}>
            <Icon name="open-in-new" size={18} color={theme.textOnSurface} />
          </YStack>
        ) : null}
      </XStack>
    </Pressable>
  );
}
